import isEmpty from "lodash/isEmpty";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useFilterManager } from "web/react/components/feeds-filters/hooks/use-filter-manager";
import {
    openFeedsFiltersOverlay,
    useOverlayHandler,
} from "web/react/components/feeds-filters/hooks/use-overlay-handler";
import { useSelectedFilterInfo } from "web/react/components/feeds-filters/hooks/use-selected-filter-info";
import { PendingFilters } from "web/react/components/feeds-filters/pending-filters";
import { FilterContentContainer } from "web/react/components/filters-emo/filter-content-container/filter-content-container";
import {
    FILTER_SECTION,
    getServerSideFilterLinks,
} from "web/react/components/filters-emo/filter-utils";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import { Text } from "web/react/emo/text";
import { View } from "web/react/emo/view";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import { ReduxStoreState } from "web/redux/store";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import { AllSelectedFilterInfo } from "web/types/feed-filters";
import { SortOptionSerializer, UniversalFilterOptionSerializer } from "web/types/serializers";
import {
    InputFilterOption,
    UniversalFilterOption,
    UniversalFilterSection,
    UniversalFiltersData,
} from "web/types/universal-filters";
import { Counter } from "./counter";
import { CurvedPill } from "./curved-pill";
import * as styles from "./filter-container-emo.css";
import { Overlay } from "./overlay";

function FilterPill({
    filterData,
    selectedFilterId,
    onClick,
    disabled = false,
    breadCrumbCount,
    href,
}: {
    filterData: UniversalFilterSection;
    selectedFilterId: string;
    onClick: (filterData: UniversalFilterSection) => void;
    disabled: boolean;
    breadCrumbCount: number;
    href?: string;
}): JSX.Element {
    const { isMobileViewport, isTabletViewport } = useDomViewport();
    const filterIsOpen = filterData.id === selectedFilterId;
    const filterIsSelected = breadCrumbCount > 0;

    const isMoreFilterSection = filterData.id === FILTER_SECTION.moreFilter;

    const active = filterIsOpen || filterIsSelected;
    const excludeFilters = [FILTER_SECTION.saleFilter, FILTER_SECTION.moreFilter];

    function getIcon(): JSX.Element | undefined {
        if (
            filterIsSelected &&
            !excludeFilters.some((excludeFilters) => excludeFilters === filterData.id)
        ) {
            return (
                <Counter
                    number={breadCrumbCount}
                    iconCounterVariant={active ? "active" : "inactive"}
                />
            );
        }

        if (isMoreFilterSection) {
            return <SVGIcon name="filters" />;
        }
        if (disabled || filterData.id === FILTER_SECTION.saleFilter) {
            return undefined;
        }

        if (filterIsOpen) {
            return <SVGIcon className={styles.filterIconOpen} name={"chevron-emo"} />;
        } else {
            return <SVGIcon name={"chevron-emo"} />;
        }
    }

    const icon = getIcon();
    const label =
        filterData.id === FILTER_SECTION.moreFilter && (isMobileViewport || isTabletViewport)
            ? undefined
            : filterData.title;

    return (
        <CurvedPill
            label={label}
            onClick={() => onClick(filterData)}
            active={active}
            activeHighlighted={filterIsSelected}
            ariaLabel={label ? undefined : filterData.title}
            disabled={disabled}
            href={href}
            icon={icon}
        />
    );
}

interface FilterContextProps {
    canClearAll: boolean;
    updateFromServer: () => void;
    isLoading: boolean;
    onFilterItemClick: (filter: UniversalFilterOption | UniversalFilterOptionSerializer) => void;
    applyMultiple: (filters: UniversalFilterOption[] | UniversalFilterOptionSerializer[]) => void;
    pendingFilterInfo: PendingFilters;
    sortOptions: SortOptionSerializer[];
    displayedFilters: UniversalFiltersData;
    selectedFilterInfo: AllSelectedFilterInfo;
    onClearClick: (sectionToClear?: string) => void;
}

export const FilterOverlayContext = React.createContext<FilterContextProps | undefined>(undefined);

export function useFilterContext(): FilterContextProps {
    const context = React.useContext(FilterOverlayContext);

    if (!context) {
        throw new Error(`useFilterContext must be used within a useFilterContextProvider`);
    }

    return context;
}

export function extractFilterStateCount(state: ReduxStoreState): AllSelectedFilterInfo {
    return state.feedSidebarReducer.detailedCount;
}

interface FilterContainerEmoProps {
    filters: UniversalFiltersData;
    productCount: number;
    sortOptions: SortOptionSerializer[];
    shouldCloseDropdown?: boolean;
    // Fired on horizontal scroll of the pill row (e.g. CW-3710's filter.scrolled event)
    onHorizontalScroll?: () => void;
}

export function FilterContainerEmo({
    filters,
    productCount,
    sortOptions,
    shouldCloseDropdown = false,
    onHorizontalScroll,
}: FilterContainerEmoProps): JSX.Element {
    const moreFiltersSection = {
        id: FILTER_SECTION.moreFilter,
        title: gettext("feed.filters.more_filters_title"), // More Filters
    };

    const {
        displayedFilters,
        canClearAll,
        clearAllFilters,
        clearSelectedFilters,
        onClick,
        applyState,
        canApply,
        cancelState,
        updateFromServer,
        ...otherFilterProps
    } = useFilterManager(filters);
    const { isMobileViewport, isTabletViewport, isDesktopViewport } = useDomViewport();

    const [, selectedFilterInfo] = useSelectedFilterInfo(
        displayedFilters,
        otherFilterProps.pendingFilterInfo
    );
    const extractedFilterStateCount = useSelector(extractFilterStateCount);
    const filterCount = isEmpty(extractedFilterStateCount)
        ? selectedFilterInfo
        : extractedFilterStateCount;

    const [selectedSection, setSelectedSection] = useState<any>(null);
    const [isCollapsableOpen, setIsCollapsableOpen] = useState(false);

    const filterSectionList = displayedFilters.filter_section_list;
    // Iterates over each filter section and accumulates the total breadcrumb count to
    // determine whether the MoreFilters Section should be highlighted/unhighlighted
    const totalBreadCrumbCount = filterSectionList.reduce((filterTotalSum, filter) => {
        if (!filterCount[filter.id]?.breadcrumbs) return filterTotalSum;
        return filterTotalSum + filterCount[filter.id].breadcrumbs.length;
    }, 0);

    const isNoProducts = totalBreadCrumbCount === 0 && productCount === 0;

    const { applyAndClose, cancelAndClose, overlayIsOpen } = useOverlayHandler(
        applyState,
        cancelState
    );

    const moreFilterSectionIds = filterSectionList.map((section: UniversalFilterSection) => {
        if (section.render_condition === "more") {
            return section.id;
        }
    });

    const saleFilterInMainFilters =
        filterSectionList.filter(
            (section: UniversalFilterSection) =>
                section.render_condition === "main" && section.id === FILTER_SECTION.saleFilter
        ).length > 0;

    const saleFilterActive =
        filterSectionList.filter((e) => e.id === FILTER_SECTION.saleFilter).length > 0 ||
        isNoProducts;

    useEffect(() => {
        // Need to open the overlay on mobile and tablet for the sale button to work
        if (isMobileViewport || isTabletViewport) {
            setTimeout(() => {
                openFeedsFiltersOverlay();
            }, 50);
        }
    }, [selectedSection, isMobileViewport, isTabletViewport]);

    useEffect(() => {
        if (shouldCloseDropdown && isCollapsableOpen && isDesktopViewport) {
            closeCollapsable();
        }
    }, [shouldCloseDropdown, isCollapsableOpen, isDesktopViewport]);

    const onSaleFilter = filterSectionList.find(
        (filter) => filter.id === FILTER_SECTION.saleFilter
    );

    const onSaleFilterOption = onSaleFilter?.option_groups[0].options as
        | InputFilterOption[]
        | undefined;

    const filterWrapperRef = useRef<HTMLDivElement>(null);

    const isLoading = otherFilterProps.isLoading || otherFilterProps.lazyFacetIsLoading;

    function onClearAllClick(): void {
        if (!canClearAll) {
            return;
        }
        clearAllFilters();
    }

    function onClearSelectedFilterClick(section: string | string[], shouldApply = true): void {
        clearSelectedFilters(section, shouldApply);
    }

    function onClearClick(sectionToClear?, shouldApply = false): void {
        if (!!sectionToClear) {
            cancelState();
            onClearSelectedFilterClick(sectionToClear, shouldApply);
        } else if (selectedSection?.id === FILTER_SECTION.moreFilter) {
            onClearSelectedFilterClick(moreFilterSectionIds as string[]);
        } else {
            // On general clear we dont want to auto apply - as we want the user to confirm with view items CTA
            onClearSelectedFilterClick(selectedSection?.id || [], shouldApply);
        }
    }

    function onSalePillClick(): void {
        analytics.event("filter-pill", "click", "sale-pill");

        if (!onSaleFilter) return;

        let onSaleMainOption;

        const isOnSaleFilterApplied = onSaleFilterOption?.find((option) => {
            if (option.value === 1) onSaleMainOption = option; // general on sale
            return option.checked;
        });
        if (isOnSaleFilterApplied) {
            // We want the sale button to auto clear when clicked as it's a quick click item
            onClearSelectedFilterClick(onSaleFilter.id, true);
        } else if (onSaleMainOption) {
            onClick(onSaleMainOption, false, true);
        }
    }

    function onFilterPillClick(
        sectionData: UniversalFilterSection | typeof moreFiltersSection,
        productCount = 100,
        customData = {},
        filterDataCount = 0
    ): void {
        analytics.event("filter-pill", "click", sectionData.title, false, customData);

        if (productCount > 0 || filterDataCount > 0 || totalBreadCrumbCount > 0) {
            setSelectedSection(sectionData);
            setIsCollapsableOpen(true);
            analytics.event("filter", "open");
        }
    }

    function navigateToTopFilter(filterId: string): void {
        const filterData = filterSectionList?.find((pill) => pill.id === filterId);
        filterData && onFilterPillClick(filterData);
    }

    function closeCollapsable(): void {
        setSelectedSection(null);
        setIsCollapsableOpen(false);
        analytics.event("filter", "close");
    }

    function getFilterContentContainer(): JSX.Element | null {
        return selectedSection ? (
            <div data-testid={`container-${selectedSection.id}`}>
                <FilterContentContainer
                    activeFilterId={selectedSection.id}
                    clearSelectedFilters={onClearSelectedFilterClick}
                    navigateToTopFilter={navigateToTopFilter}
                    onClearAllClick={onClearAllClick}
                    {...otherFilterProps}
                />
            </div>
        ) : null;
    }

    function onFilterItemClick(
        filter: UniversalFilterOption | UniversalFilterOptionSerializer
    ): void {
        onClick(filter, false, false);
    }

    const { seoCategoryLinks, seoColorLinks } =
        !isCollapsableOpen && getServerSideFilterLinks(filterSectionList);

    function getSaleButton(): JSX.Element | undefined {
        const saleBreadCrumbCount = filterCount[onSaleFilter?.id ?? ""]?.breadcrumbs.length ?? 0;
        const saleUrl = onSaleFilterOption?.find((option) => option.value === 1)?.url;

        if (onSaleFilter) {
            return saleFilterActive ? (
                <FilterPill
                    onClick={onSalePillClick}
                    filterData={onSaleFilter}
                    selectedFilterId={selectedSection?.id}
                    disabled={onSaleFilterOption?.length === 0}
                    breadCrumbCount={saleBreadCrumbCount}
                    href={saleUrl}
                />
            ) : (
                <FilterPill
                    onClick={onSalePillClick}
                    filterData={onSaleFilter}
                    selectedFilterId={selectedSection?.id}
                    disabled={onSaleFilterOption?.length === 0}
                    breadCrumbCount={saleBreadCrumbCount}
                />
            );
        }
    }

    return (
        <FilterOverlayContext.Provider
            value={{
                canClearAll,
                updateFromServer,
                isLoading,
                pendingFilterInfo: otherFilterProps.pendingFilterInfo,
                onFilterItemClick,
                applyMultiple: otherFilterProps.applyMultiple,
                sortOptions: sortOptions,
                displayedFilters,
                selectedFilterInfo: otherFilterProps.selectedFilterInfo,
                onClearClick: onClearClick,
            }}
        >
            <div ref={filterWrapperRef} data-testid="filter-container">
                <View width={"full"} marginBottom={"sm"}>
                    <div className={styles.wrapper} onScroll={onHorizontalScroll}>
                        <div className={styles.filtersWrapper}>
                            {(isMobileViewport || isTabletViewport) && (
                                <FilterPill
                                    onClick={() =>
                                        onFilterPillClick(moreFiltersSection, 100, {
                                            moreFilterType: "left-side",
                                        })
                                    }
                                    filterData={moreFiltersSection as UniversalFilterSection}
                                    selectedFilterId={selectedSection?.id}
                                    disabled={isNoProducts}
                                    breadCrumbCount={totalBreadCrumbCount}
                                />
                            )}
                            {!saleFilterInMainFilters && getSaleButton()}
                            <section className={styles.mainFilters}>
                                {filterSectionList
                                    .filter((filterData) => filterData.render_condition === "main")
                                    .map((filterData) => {
                                        const isNoDisplayFilters =
                                            !displayedFilters.filter_section_list.some(
                                                (section) => section.id === filterData.id
                                            );
                                        const isSizeFilter =
                                            filterData.id === FILTER_SECTION.sizeFilter;
                                        const isCategoryFilter =
                                            filterData.id === FILTER_SECTION.categoryFilter;

                                        const isColorFilter =
                                            filterData.id === FILTER_SECTION.colorFilter;

                                        const isSeoTaxonomyFilterValid =
                                            productCount >= 48 &&
                                            [
                                                FILTER_SECTION.taxonomyDressStyleFilter,
                                                FILTER_SECTION.taxonomyDressLengthFilter,
                                                FILTER_SECTION.taxonomyDressOccasionFilter,
                                                FILTER_SECTION.taxonomyDressNecklineFilter,
                                                FILTER_SECTION.taxonomyDressSleeveFilter as string,
                                            ].includes(filterData.id);

                                        const isSaleFilter =
                                            filterData.id === FILTER_SECTION.saleFilter;

                                        // This is a solution in the frontend that should be moved to backend
                                        // Ticket for a deeper solution is CW-1488
                                        const isValidPill =
                                            (isColorFilter &&
                                                filterData.option_groups.length >= 1 &&
                                                filterData.option_groups[0]?.id ===
                                                    "option-group-color") ||
                                            isSeoTaxonomyFilterValid ||
                                            !isColorFilter;

                                        const breadCrumbCount =
                                            filterCount[filterData.id]?.breadcrumbs.length | 0;

                                        if (isValidPill) {
                                            if (isSaleFilter) {
                                                return (
                                                    <div
                                                        key={filterData.id}
                                                        data-testid={`pill-${filterData.id}`}
                                                    >
                                                        {getSaleButton()}
                                                    </div>
                                                );
                                            } else {
                                                return (
                                                    <div
                                                        key={filterData.id}
                                                        data-testid={`pill-${filterData.id}`}
                                                    >
                                                        {isCategoryFilter && seoCategoryLinks}
                                                        {isColorFilter && seoColorLinks}
                                                        <FilterPill
                                                            onClick={(filterData) =>
                                                                onFilterPillClick(
                                                                    filterData,
                                                                    100,
                                                                    {},
                                                                    breadCrumbCount
                                                                )
                                                            }
                                                            filterData={filterData}
                                                            selectedFilterId={selectedSection?.id}
                                                            disabled={
                                                                !isSizeFilter &&
                                                                (isNoProducts || isNoDisplayFilters)
                                                            }
                                                            breadCrumbCount={breadCrumbCount}
                                                        />
                                                    </div>
                                                );
                                            }
                                        }
                                    })}
                            </section>
                            {isMobileViewport && (
                                <div className={styles.allFiltersWrapper}>
                                    <button
                                        className={styles.edgeButton}
                                        onClick={() =>
                                            onFilterPillClick(moreFiltersSection, 100, {
                                                moreFilterType: "right-side",
                                            })
                                        }
                                    >
                                        <Text
                                            textStyle="body-s"
                                            className={styles.clearAllBtn["active"]}
                                        >
                                            {
                                                // All filters
                                                gettext("feed.filter.all_filters")
                                            }
                                        </Text>
                                    </button>
                                </div>
                            )}
                            {isDesktopViewport && (
                                <>
                                    <button className={styles.edgeButton} onClick={onClearAllClick}>
                                        <Text
                                            textStyle="body-s"
                                            className={
                                                styles.clearAllBtn[
                                                    canClearAll ? "active" : "inactive"
                                                ]
                                            }
                                        >
                                            {
                                                // Clear All
                                                gettext("feed.filters.mobile_clear_all_btn_text")
                                            }
                                        </Text>
                                    </button>
                                    {productCount !== 0 && (
                                        <section className={styles.moreFilterPill}>
                                            <FilterPill
                                                onClick={() =>
                                                    onFilterPillClick(moreFiltersSection, 100, {
                                                        moreFilterType: "right-side",
                                                    })
                                                }
                                                filterData={
                                                    moreFiltersSection as UniversalFilterSection
                                                }
                                                selectedFilterId={selectedSection?.id}
                                                disabled={isNoProducts}
                                                breadCrumbCount={totalBreadCrumbCount}
                                            />
                                        </section>
                                    )}
                                </>
                            )}
                        </div>
                        {selectedSection && (
                            <Overlay
                                isOpen={isCollapsableOpen}
                                sectionTitle={selectedSection.title}
                                isMoreFiltersSelected={
                                    selectedSection.id === FILTER_SECTION.moreFilter
                                }
                                closeCollapsable={closeCollapsable}
                                canApply={canApply}
                                applyAndClose={applyAndClose}
                                cancelAndClose={cancelAndClose}
                                cancelStateUpdate={cancelState}
                                overlayIsOpen={overlayIsOpen}
                                filterBreadcrumb={filterCount[selectedSection.id]?.breadcrumbs}
                            >
                                {getFilterContentContainer()}
                            </Overlay>
                        )}
                    </div>
                </View>
            </div>
        </FilterOverlayContext.Provider>
    );
}
